/**
 * MasterDataCalendarFormDialog — Create/Edit `market_calendar` row.
 *
 * Compact Sales Contract style: title bar with inline Save/Cancel,
 * tight Grid layout, shaded read-only PK fields in edit mode.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateCalendarMutation,
  useUpdateCalendarMutation,
} from '@/store/api/refsCrudApi'
import type { MarketCalendar } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogTitleSx,
  numSx,
  roSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MarketCalendar | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  code: string
  ex_text: string
  year: string
  month: string
  ex: string
  full_symbol: string
  first_business_day: string
  last_business_day: string
  first_notice_day: string
  last_trading_day: string
  last_fixing_day: string
}

const EMPTY: FormState = {
  code: '',
  ex_text: '',
  year: '',
  month: '',
  ex: '',
  full_symbol: '',
  first_business_day: '',
  last_business_day: '',
  first_notice_day: '',
  last_trading_day: '',
  last_fixing_day: '',
}

const fromRow = (r: MarketCalendar): FormState => ({
  code: r.code ?? '',
  ex_text: r.ex_text ?? '',
  year: r.year != null ? String(r.year) : '',
  month: r.month != null ? String(r.month) : '',
  ex: r.ex ?? '',
  full_symbol: r.full_symbol ?? '',
  first_business_day: r.first_business_day ?? '',
  last_business_day: r.last_business_day ?? '',
  first_notice_day: r.first_notice_day ?? '',
  last_trading_day: r.last_trading_day ?? '',
  last_fixing_day: r.last_fixing_day ?? '',
})

const toPayload = (f: FormState): Partial<MarketCalendar> => ({
  year: f.year ? Number(f.year) : null,
  month: f.month ? Number(f.month) : null,
  ex: f.ex.trim() || null,
  full_symbol: f.full_symbol.trim() || null,
  first_business_day: f.first_business_day || null,
  last_business_day: f.last_business_day || null,
  first_notice_day: f.first_notice_day || null,
  last_trading_day: f.last_trading_day || null,
  last_fixing_day: f.last_fixing_day || null,
})

export default function MasterDataCalendarFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createCalendar, { isLoading: creating }] = useCreateCalendarMutation()
  const [updateCalendar, { isLoading: updating }] = useUpdateCalendarMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (!open) return
    setTouched(false)
    setForm(isEdit && initialValue ? fromRow(initialValue) : EMPTY)
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.code.trim()
  const exTextEmpty = !form.ex_text.trim()
  const canSave = !codeEmpty && !exTextEmpty && !loading

  const handleSave = async () => {
    if (codeEmpty || exTextEmpty) { setTouched(true); return }
    try {
      const body = toPayload(form)
      if (isEdit && initialValue) {
        await updateCalendar({
          code: initialValue.code,
          ex_text: initialValue.ex_text,
          body,
        }).unwrap()
        snack.success('Calendar updated')
      } else {
        await createCalendar({
          code: form.code.trim(),
          ex_text: form.ex_text.trim(),
          ...body,
        } as MarketCalendar).unwrap()
        snack.success('Calendar created')
      }
      onSaved?.()
      onClose()
    } catch (e2) {
      snack.error(getErrorMessage(e2, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  // Shared slotProps builder for DatePicker fields with explicit tab order.
  const dateSlot = (tab: number) => ({
    textField: {
      size: 'small' as const,
      fullWidth: true,
      InputLabelProps: { shrink: true },
      inputProps: { tabIndex: tab },
    },
  })

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" sx={{ flex: 1, fontWeight: 700, fontSize: '1rem' }}>
          {isEdit ? 'Edit Calendar' : 'New Calendar'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.code} / {initialValue.ex_text}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          <Button type="button" variant="contained" size="small" color="success" onClick={() => void handleSave()} disabled={!canSave}>
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {/* Field order follows Access ColumnOrder (0..10):
            Year, Month, Code, First Business Day, Last Business Day,
            First Notice Day, Last Trading Day, Last Fixing Day,
            Exchange, Exchange Text, Full symbol. Composite PK = code + ex_text
            (read-only in edit mode). */}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          {/* ColumnOrder 0-1 — Year, Month (plain integers, right-aligned) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Year"
              type="number"
              size="small"
              fullWidth
              value={form.year}
              onChange={(e) => set('year', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 1, style: { textAlign: 'right' } }}
              sx={numSx}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Month"
              type="number"
              size="small"
              fullWidth
              value={form.month}
              onChange={(e) => set('month', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2, style: { textAlign: 'right' } }}
              sx={numSx}
            />
          </Grid>

          {/* ColumnOrder 2 — Code (PK) */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Code"
              required
              error={touched && codeEmpty}
              size="small"
              fullWidth
              value={form.code}
              onChange={(e) => set('code', e.target.value)}
              disabled={isEdit}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 3 }}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          {/* ColumnOrder 9 — Exchange Text (PK); grouped next to Code */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Exchange Text"
              required
              error={touched && exTextEmpty}
              size="small"
              fullWidth
              value={form.ex_text}
              onChange={(e) => set('ex_text', e.target.value)}
              disabled={isEdit}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 10 }}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>

          {/* ColumnOrder 3-7 — Dates */}
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="First Business Day"
              format="DD-MMM-YYYY"
              value={form.first_business_day ? dayjs(form.first_business_day) : null}
              onChange={(d) => set('first_business_day', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={dateSlot(4)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="Last Business Day"
              format="DD-MMM-YYYY"
              value={form.last_business_day ? dayjs(form.last_business_day) : null}
              onChange={(d) => set('last_business_day', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={dateSlot(5)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="First Notice Day"
              format="DD-MMM-YYYY"
              value={form.first_notice_day ? dayjs(form.first_notice_day) : null}
              onChange={(d) => set('first_notice_day', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={dateSlot(6)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="Last Trading Day"
              format="DD-MMM-YYYY"
              value={form.last_trading_day ? dayjs(form.last_trading_day) : null}
              onChange={(d) => set('last_trading_day', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={dateSlot(7)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <DatePicker
              label="Last Fixing Day"
              format="DD-MMM-YYYY"
              value={form.last_fixing_day ? dayjs(form.last_fixing_day) : null}
              onChange={(d) => set('last_fixing_day', d && d.isValid() ? d.format('YYYY-MM-DD') : '')}
              slotProps={dateSlot(8)}
            />
          </Grid>

          {/* ColumnOrder 8 — Exchange */}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Exchange"
              size="small"
              fullWidth
              value={form.ex}
              onChange={(e) => set('ex', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 9 }}
            />
          </Grid>
          {/* ColumnOrder 10 — Full symbol */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Full symbol"
              size="small"
              fullWidth
              value={form.full_symbol}
              onChange={(e) => set('full_symbol', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 11 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
